<template>
  <div>
    <data-table
      ref="datatableRef"
      data-testid="servicos-datatable"
      :config="datatableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :adjustColumnsWidth="adjustColumnsWidthConfig"
      :pdfButton="pdfButtonConfig"
      :csvButton="csvButtonConfig"
      :clipboardButton="clipboardButtonConfig"
      :printButton="printButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @row-click="(e: any) => handleRowClick(e)"
      @edit-click="handleEditClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';

interface ServicoRow {
  servico_id: number | string;
  servico_nome?: string;
  categoria?: string;
  servico_valor_padrao?: number;
  [key: string]: unknown;
}

interface DataTableExposed {
  loadData: () => void;
}

const { t } = useI18n();

const datatableRef = ref<DataTableExposed | null>(null);

const emit = defineEmits<{
  (e: 'edit', servicoId: number | string): void;
}>();

const endpoint = computed<string>(() => '/index.php/config/servicos/buscar_servicos');

const tableColumns = computed(() => {
  return [
    {
      data: 'servico_id',
      title: t('ID'),
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'clickable_id'
    },
    {
      data: 'servico_nome',
      title: t('Nome do Serviço'),
      width: 300,
      resizable: true,
      sortable: true,
      filterable: true
    },
    {
      data: 'categoria',
      title: t('Categoria'),
      width: 200,
      resizable: true,
      sortable: true,
      filterable: true
    },
    {
      data: 'servico_valor_padrao',
      title: t('Valor Padrão'),
      width: 150,
      resizable: true,
      sortable: true,
      renderType: 'currency'
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    tableId: 'tabelaServicos',
    title: t('Serviços'),
    filename: `servicos_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 25,
    height: '500px',
    footer: false,
    rowSelection: 'single',
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.servico_id;
      return id == null ? '' : `servicos-row-${id as number | string}`;
    }
  };
});

const filterConfig = computed(() => ({
  enabled: true
}));

const refreshButtonConfig = computed(() => ({
  show: true
}));

const adjustColumnsWidthConfig = computed(() => ({
  show: true
}));

const pdfButtonConfig = computed(() => ({
  show: true
}));

const csvButtonConfig = computed(() => ({
  show: true
}));

const clipboardButtonConfig = computed(() => ({
  show: true
}));

const printButtonConfig = computed(() => ({
  show: true
}));

const loadData = (): void => {
  log('ServicosTable', 'loadData', 'Triggering data reload');
  if (datatableRef.value) {
    datatableRef.value.loadData();
  }
};

const handleDataLoaded = (data: ServicoRow[] | undefined): void => {
  log('ServicosTable', 'handleDataLoaded', 'Data loaded successfully', data?.length);
};

const handleDataError = (error: unknown): void => {
  log('ServicosTable', 'handleDataError', 'Error loading data', error);
  eventBus.emit('globalMessage.error', t('Erro ao carregar serviços'));
};

const handleRowClick = (rowData: ServicoRow | null | undefined): void => {
  log('ServicosTable', 'handleRowClick', 'Row clicked', rowData);
  if (rowData && rowData.servico_id) {
    emit('edit', rowData.servico_id);
  }
};

const handleEditClick = (servicoId: number | string | null | undefined): void => {
  log('ServicosTable', 'handleEditClick', 'Edit clicked', servicoId);
  if (servicoId) {
    emit('edit', servicoId);
  }
};

const handleRefresh = (): void => {
  log('ServicosTable', 'handleRefresh', 'Refresh event received');
  loadData();
};

onMounted(() => {
  log('ServicosTable', 'mounted', 'Component mounted');
  eventBus.on('servicos.refresh', handleRefresh);
});

onBeforeUnmount(() => {
  log('ServicosTable', 'beforeUnmount', 'Component unmounting');
  eventBus.off('servicos.refresh', handleRefresh);
});

defineExpose({
  loadData
});
</script>

<style scoped>
/* Styles are handled by data-table component */
</style>
